import React from "react";
import { Box, Tooltip } from "@mui/material";

interface BudgetSegment {
  label: string;
  amount: number;
  color: string;
}

interface AiCreditsProgressBarProps {
  baseline: number;
  projectBudgets: { projectId: string; projectName: string; budget: number }[];
  currentUsage: number;
}

const SEGMENT_COLORS = [
  "#1976d2", // baseline - blue
  "#2e7d32", // green
  "#ed6c02", // orange
  "#9c27b0", // purple
  "#d32f2f", // red
  "#0288d1", // light blue
  "#558b2f", // light green
];

const AiCreditsProgressBar: React.FC<AiCreditsProgressBarProps> = ({
  baseline,
  projectBudgets,
  currentUsage,
}) => {
  const segments: BudgetSegment[] = [
    {
      label: "Included in license",
      amount: baseline,
      color: SEGMENT_COLORS[0],
    },
    ...projectBudgets.map((pb, i) => ({
      label: pb.projectName || pb.projectId,
      amount: pb.budget,
      color: SEGMENT_COLORS[(i + 1) % SEGMENT_COLORS.length],
    })),
  ];

  const totalBudget = segments.reduce((sum, s) => sum + s.amount, 0);
  const fillPercentage =
    totalBudget > 0 ? Math.min((currentUsage / totalBudget) * 100, 100) : 0;

  return (
    <Box sx={{ mt: 1, mb: 1 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
        <Box sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
          Current AI Credits Usage
        </Box>
        <Box sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>
          ${currentUsage.toFixed(2)} / ${totalBudget.toFixed(2)}
        </Box>
      </Box>

      {/* Progress bar container */}
      <Box
        sx={{
          position: "relative",
          height: 24,
          borderRadius: 1,
          overflow: "hidden",
          bgcolor: "#e0e0e0",
          display: "flex",
        }}
      >
        {/* Segments background */}
        {segments.map((segment, index) => {
          const segmentWidth =
            totalBudget > 0 ? (segment.amount / totalBudget) * 100 : 0;
          return (
            <Tooltip
              key={index}
              title={`${segment.label}: $${segment.amount.toFixed(2)}`}
              arrow
            >
              <Box
                sx={{
                  width: `${segmentWidth}%`,
                  height: "100%",
                  bgcolor: `${segment.color}33`,
                  borderRight:
                    index < segments.length - 1 ? "1px solid #fff" : "none",
                  position: "relative",
                }}
              />
            </Tooltip>
          );
        })}

        {/* Fill overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            height: "100%",
            width: `${fillPercentage}%`,
            background: `linear-gradient(90deg, ${SEGMENT_COLORS[0]}cc, ${
              fillPercentage > (baseline / totalBudget) * 100
                ? SEGMENT_COLORS[1] + "cc"
                : SEGMENT_COLORS[0] + "cc"
            })`,
            borderRadius: fillPercentage >= 100 ? 1 : "4px 0 0 4px",
            transition: "width 0.5s ease-in-out",
          }}
        />
      </Box>

      {/* Legend */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 0.5 }}>
        {segments.map((segment, index) => (
          <Box
            key={index}
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: segment.color,
              }}
            />
            <Box sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
              {segment.label}: ${segment.amount.toFixed(2)}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default AiCreditsProgressBar;
